"use client";

import Pagination from "@mui/material/Pagination";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import DataTable from "examples/Tables/DataTable";
import { useContext } from "react";
import useFilterParams from "../../../receipts/useFilterParams";
import { downloadReceiptPdf } from "../../../../Classes/API/ReceiptsApi";
import { LanguageContext } from "App";

const BalanceHistoryTable = ({
  balanceHistory,
  totalCount,
  pageNumber,
  pageSize,
}) => {
  const { onFilterChange, getInitialFilters } = useFilterParams();
  const { translations } = useContext(LanguageContext);

  const filters = getInitialFilters();

  const handleInputChange = (e, value) => {
    const updatedFilters = { ...filters, page: value };
    onFilterChange(updatedFilters);
  };

  const TableColumns = [
    {
      Header: translations.TransactionSource,
      accessor: "transactionSource",
      align: "left",
      Cell: ({ value }) => (
        <MDBox
          sx={{
            backgroundColor:
              value === "Payment"
                ? "#e8f5e8"
                : value === "User Balance"
                ? "#ffebee"
                : "transparent",
            borderRadius: "8px",
            padding: "8px 12px",
            border:
              value === "Payment"
                ? "1px solid #4caf50"
                : value === "User Balance"
                ? "1px solid #f44336"
                : "none",
            display: "inline-block",
            minWidth: "120px",
            textAlign: "center",
          }}
        >
          <MDTypography
            variant="button"
            fontWeight="medium"
            sx={{
              color:
                value === "Payment"
                  ? "#2e7d32"
                  : value === "User Balance"
                  ? "#d32f2f"
                  : "inherit",
            }}
          >
            {value}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: translations.UserAccountingEntryId,
      accessor: "userAccountingEntryId",
      align: "left",
    },
    {
      Header: translations.ReceiptId,
      accessor: "receiptId",
      align: "left",
    },
    {
      Header: translations.appointmentAppointmentsScheduleDate,
      accessor: "date",
      align: "left",
    },
    {
      Header: translations.CreateAccountingEntrypgAmount,
      accessor: "amount",
      align: "left",
      Cell: ({ value }) => (
        <MDTypography variant="button" fontWeight="medium">
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "",
      accessor: "view",
      align: "center",
    },
  ];

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getTransactionSourceText = (source) => {
    if (source === "Payment") return translations.Payment;
    if (source === "User Balance") return translations.UserBalance;
    return source;
  };

  return (
    <>
      <MDBox
        py={4}
        px={3}
        sx={{
          borderTopLeftRadius: "1.1rem",
          borderTopRightRadius: "1.1rem",
          backgroundColor: "#3560AD",
        }}
        align="center"
      >
        <MDTypography variant="h3" color="white" fontWeight="bold" mb={1}>
          {translations.BalanceHistoryTable}
        </MDTypography>
        <MDBox
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            borderRadius: "12px",
            padding: "8px 16px",
            display: "inline-block",
          }}
        >
          <MDTypography variant="h5" color="white" fontWeight="medium">
            Total Records: {totalCount}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox
        p={3}
        sx={{
          backgroundColor: "#ffffff",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        
        }}
      >
        <DataTable
          table={{
            columns: TableColumns,
            rows: balanceHistory.map((item) => ({
              transactionSource: getTransactionSourceText(
                item.transactionSource
              ),
              userAccountingEntryId: item.userAccountingEntryId,
              receiptId: item.receiptId,
              date: new Date(item.date).toLocaleDateString(),
              amount: formatAmount(item.amount),
              view: item.receiptId ? (
                <MDBox
                  sx={{
                    backgroundColor: "#e3f2fd",
                    borderRadius: "8px",
                    padding: "8px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#bbdefb",
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(33, 150, 243, 0.3)",
                    },
                  }}
                >
                  <MDTypography
                    component="button"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    sx={{
                      minWidth: "auto",
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      cursor: "pointer",
                      padding: 0,
                      margin: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    onClick={async () => {
                      try {
                        const blob = await downloadReceiptPdf(item.receiptId);
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `receipt-${item.receiptId}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } catch (error) {
                        console.error("Download failed:", error);
                      }
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      width="1.25em"
                      height="1.25em"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                      />
                    </svg>
                    Download
                  </MDTypography>
                </MDBox>
              ) : (
                <MDTypography
                  component="span"
                  variant="button"
                  color="text"
                  fontWeight="medium"
                  sx={{
                    opacity: 0.5,
                    backgroundColor: "#f5f5f5",
                    padding: "4px 8px",
                    borderRadius: "4px",
                  }}
                >
                  N/A
                </MDTypography>
              ),
            })),
          }}
          isSorted={false}
          entriesPerPage={false}
          showTotalEntries={false}
          noEndBorder
        />

        <MDBox
          sx={{
            marginTop: "24px",
            padding: "16px",
            backgroundColor: "#fafafa",
            borderRadius: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
          }}
        >
          <MDBox>
            <MDTypography variant="body2" color="text" fontWeight="medium">
              Showing{" "}
              <MDTypography
                component="span"
                variant="body2"
                color="info"
                fontWeight="bold"
              >
                {pageSize * (pageNumber - 1) + 1} -{" "}
                {Math.min(pageSize * pageNumber, totalCount)}
              </MDTypography>{" "}
              of{" "}
              <MDTypography
                component="span"
                variant="body2"
                color="info"
                fontWeight="bold"
              >
                {totalCount}
              </MDTypography>
            </MDTypography>
          </MDBox>
          <Pagination
            page={pageNumber}
            count={Math.ceil(totalCount / pageSize)}
            color="primary"
            onChange={handleInputChange}
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "8px",
                fontWeight: "medium",
              },
              "& .Mui-selected": {
                backgroundColor: "#3560AD !important",
                color: "white",
              },
            }}
          />
        </MDBox>
      </MDBox>
    </>
  );
};

export default BalanceHistoryTable;
